import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { WorkspaceFormValues } from '@/lib/validations/workspace'
import { workspaceService, userService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { PageContainer, PageHeader } from '@/components/layout'
import { User } from '@/types'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'
import { WorkspaceForm } from '@/components/workspaces/WorkspaceForm'

export default function WorkspaceCreatePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [logo, setLogo] = useState<string | undefined>()
  const [users, setUsers] = useState<User[]>([])
  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty,
  })

  // Load all users except the current user
  useEffect(() => {
    const loadUsers = async () => {
      if (!user) return

      try {
        const allUsers = await userService.getAll()
        // Filter out the current user since they'll be the creator
        const otherUsers = allUsers.filter((u) => u.id !== user.id)
        setUsers(otherUsers)
      } catch (error) {
        console.error('Failed to load users:', error)
        toast.error('Failed to load users')
      }
    }

    loadUsers()
  }, [user])

  const handleAddUser = (userId: string) => {
    const userToAdd = users.find((u) => u.id === userId)
    if (userToAdd && !selectedUsers.some((u) => u.id === userId)) {
      setSelectedUsers([...selectedUsers, userToAdd])
    }
  }

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter((u) => u.id !== userId))
  }

  const onSubmit = async (data: WorkspaceFormValues) => {
    if (!user) {
      toast.error('You must be logged in to create a workspace')
      return
    }

    setIsLoading(true)
    try {
      const workspace = await workspaceService.createWorkspace(
        {
          ...data,
          logo,
        },
        user.id
      )

      // Add selected users to the workspace
      if (selectedUsers.length > 0) {
        try {
          for (const selectedUser of selectedUsers) {
            await workspaceService.addUser(workspace.id, selectedUser.id)
          }
        } catch (error) {
          console.error('Failed to add some users to workspace:', error)
          toast.warning('Workspace created but some users could not be added')
        }
      }

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      toast.success('Workspace created successfully')
      navigate(`/workspaces/${workspace.id}`)
    } catch {
      toast.error('Failed to create workspace')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title="Create New Workspace" />

      <WorkspaceForm
        currentUser={user ?? undefined}
        logo={logo}
        onLogoChange={setLogo}
        usersToAdd={selectedUsers}
        usersToRemove={[]}
        availableUsers={users}
        onAddUser={handleAddUser}
        onRemoveUserToAdd={handleRemoveUser}
        onSubmit={onSubmit}
        onCancel={() => navigate('/workspaces')}
        submitText={isLoading ? 'Creating...' : 'Create Workspace'}
        loading={isLoading}
        title="Workspace Details"
        description="Create a new workspace to organize your work"
        onDirtyChange={setIsDirty}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
